import {useCallback, useState} from 'react';

import {useApi} from '~/modules/api';
import {getErrorMessage} from '~/modules/utils';

type ResendState =
  | {status: 'idle'}
  | {status: 'submitting'}
  | {status: 'success'}
  | {status: 'error'; message: string};

const RESEND_ERROR_MESSAGES: Record<string, string> = {
  rate_limited:
    'A verification email was already sent to this user recently — please wait before resending.',
  send_failed: 'The email failed to send. Please try again.',
};

const getResendErrorMessage = (message?: string) => {
  if (!message) {
    return 'Failed to resend verification email';
  }
  return RESEND_ERROR_MESSAGES[message] ?? message;
};

export const useResendVerificationEmail = () => {
  const api = useApi();
  const [resendState, setResendState] = useState<ResendState>({
    status: 'idle',
  });

  const resendVerificationEmail = useCallback(
    async (userId: string) => {
      setResendState({status: 'submitting'});

      try {
        const {data} = await api.fetch({
          url: '/users/resendVerificationEmail',
          method: 'POST',
          data: {userId},
        });

        if (data.status !== 'success') {
          throw new Error(data.errorMessage);
        }

        setResendState({status: 'success'});
      } catch (err) {
        setResendState({
          status: 'error',
          message: getResendErrorMessage(getErrorMessage(err, '')),
        });
      }
    },
    [api],
  );

  return {resendState, resendVerificationEmail};
};
